var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable @typescript-eslint/naming-convention */
/**
 * API service for AI Review Inspections data fetching
 */
import api from '@utils/axios';
import kendoDatatableParams from '../../../helpers/kendoDatatableParams';
class InspectionsReviewService {
    /**
     * Fetches AI review inspections data from the API
     * @param params - The parameters for the API call
     * @returns Promise with the inspections data
     */
    static getInspectionsReview(params) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const _a = params.filters.values, { asset } = _a, valuesFiltered = __rest(_a, ["asset"]);
                const queryParams = kendoDatatableParams({
                    page: params.page,
                    sort: params.sort,
                    filters: Object.assign(Object.assign({}, params.filters), { values: valuesFiltered }),
                    optionalParams: asset && Array.isArray(asset) && { asset: asset.join(',') }
                });
                const response = yield api.get(this.BASE_URL, {
                    params: queryParams
                });
                return response.data;
            }
            catch (error) {
                console.error('Error fetching AI review inspections:', error);
                throw error;
            }
        });
    }
    /**
     * Completes the review for an inspection by updating its status
     * @param params - The inspection ID and status ID to update
     * @returns Promise with the updated inspection data
     */
    static completeReview(params) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield api.patch(`${this.COMPLETE_REVIEW_URL}/${params.inspectionId}/complete_review`, {
                    inspection_id: params.inspectionId,
                    status_id: params.statusId
                });
            }
            catch (error) {
                console.error('Error completing inspection review:', error);
                throw error;
            }
        });
    }
    /**
     * Reopens a completed review so the office regains Send For Correction / Complete Review.
     * Resets iim_review_status from iim_completed back to iim_not_started (IP-14971).
     * @param params - The inspection ID to reopen
     * @returns Promise that resolves once the review is reopened
     */
    static reopenReview(params) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield api.patch(`${this.COMPLETE_REVIEW_URL}/${params.inspectionId}/reopen_review`, {
                    inspection_id: params.inspectionId
                });
            }
            catch (error) {
                console.error('Error reopening inspection review:', error);
                throw error;
            }
        });
    }
    /**
     * Completes the review for multiple inspections by dismissing all issues and updating status
     * @param params - The inspection IDs and status ID to update
     * @returns Promise with the bulk operation result
     */
    static bulkCompleteReview(params) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const body = Object.assign({ inspection_ids: params.inspectionIds }, (params.statusId !== null ? { status_id: params.statusId } : {}));
                const response = yield api.patch(`${this.COMPLETE_REVIEW_URL}/bulk_complete_review`, body);
                return response.data;
            }
            catch (error) {
                console.error('Error completing bulk inspection review:', error);
                throw error;
            }
        });
    }
    /**
     * Fetches all inspection IDs matching the current filters (for select all operations)
     * @param filters - The current filter configuration
     * @returns Promise with all matching inspection IDs
     */
    static getAllInspectionIds(filters) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const _a = filters.values, { asset } = _a, valuesFiltered = __rest(_a, ["asset"]);
                const queryParams = kendoDatatableParams({
                    page: { skip: 0, take: 0 }, // take: 0 signals we want IDs only
                    sort: [],
                    filters: Object.assign(Object.assign({}, filters), { values: valuesFiltered }),
                    optionalParams: asset && Array.isArray(asset) ? { asset: asset.join(','), ids_only: true } : { ids_only: true }
                });
                const response = yield api.get(`${this.BASE_URL}/all_ids`, {
                    params: queryParams
                });
                return response.data;
            }
            catch (error) {
                console.error('Error fetching all inspection IDs:', error);
                throw error;
            }
        });
    }
}
InspectionsReviewService.BASE_URL = '/inspection_assistant_review/datatable';
InspectionsReviewService.COMPLETE_REVIEW_URL = '/inspection_assistant_review';
export default InspectionsReviewService;
